import { useState, useEffect } from "react";
import { Loader2, Search, User, TrendingUp, X, MessageCircle, Shield, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/theme-toggle";
import { MobileNav } from "@/components/mobile-nav";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/router";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { isValidInstagramUsername } from "@/lib/instagram-service";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { motion, useAnimation } from "framer-motion";

import {
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    Tooltip,
    YAxis,
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    Radar
} from "recharts";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from "@/components/ui/chart";

// 서비스 신뢰도 차트 데이터
const trustChartData = [
    { month: "1월", 안정성: 92, 보안성: 96, 이용자만족도: 88 },
    { month: "2월", 안정성: 93, 보안성: 97, 이용자만족도: 90 },
    { month: "3월", 안정성: 94, 보안성: 98, 이용자만족도: 93 },
    { month: "4월", 안정성: 96, 보안성: 98, 이용자만족도: 94 },
    { month: "5월", 안정성: 97, 보안성: 99, 이용자만족도: 95 }
];

// 이용 통계 차트 데이터
const usageChartData = [
    { category: "메시지 복구율", value: 97 },
    { category: "사용자 정보 보호", value: 99 },
    { category: "서비스 가용성", value: 99.8 },
    { category: "메시지 검색 정확도", value: 95 },
    { category: "API 응답 속도", value: 96 },
    { category: "고객 만족도", value: 94 }
];

// 차트 설정
const trustChartConfig = {
    안정성: {
        label: "시스템 안정성",
        color: "hsl(var(--primary))"
    },
    보안성: {
        label: "보안 수준",
        color: "hsl(var(--accent))"
    },
    이용자만족도: {
        label: "이용자 만족도",
        color: "hsl(var(--success))"
    }
} satisfies ChartConfig;

// 타이프라이터 플레이스홀더
const typewriterPlaceholders = [
    "인스타그램 ID 입력",
    "메시지 확인할 인스타 계정 ID 검색",
    "내 계정온 메시지 확인",
];

/**
 * 인스타그램 메시지 페이지 컴포넌트
 */
export function MessagesPage() {
    const [username, setUsername] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [searchCompleted, setSearchCompleted] = useState<boolean>(false);
    const { toast } = useToast();
    const { session } = useAuth();
    const navigate = useNavigate();

    // Framer Motion 애니메이션 컨트롤러
    const buttonControls = useAnimation();
    const cardControls = useAnimation();

    // 타이프라이터 효과 상태
    const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
    const [currentPlaceholder, setCurrentPlaceholder] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [pauseTime] = useState<number>(1000);

    // Framer Motion 애니메이션 컨트롤러

    // 타이프라이터 효과 구현
    useEffect(() => {
        const currentText = typewriterPlaceholders[placeholderIndex];
        let timeout: NodeJS.Timeout;

        if (isDeleting) {
            if (currentPlaceholder === "") {
                setIsDeleting(false);
                setPlaceholderIndex(
                    (prev) => (prev + 1) % typewriterPlaceholders.length
                );
                timeout = setTimeout(() => { }, 500);
            } else {
                timeout = setTimeout(() => {
                    setCurrentPlaceholder(currentPlaceholder.slice(0, -1));
                }, 50);
            }
        } else {
            if (currentPlaceholder === currentText) {
                timeout = setTimeout(() => {
                    setIsDeleting(true);
                }, pauseTime);
            } else {
                timeout = setTimeout(() => {
                    setCurrentPlaceholder(
                        currentText.slice(0, currentPlaceholder.length + 1)
                    );
                }, 100);
            }
        }

        return () => clearTimeout(timeout);
    }, [currentPlaceholder, isDeleting, placeholderIndex, pauseTime]);

    // 버튼 애니메이션 효과
    useEffect(() => {
        const glowingAnimation = async (): Promise<void> => {
            await buttonControls.start({
                boxShadow: [
                    "0 0 5px rgba(59, 130, 246, 0.5)",
                    "0 0 10px rgba(59, 130, 246, 0.7)",
                    "0 0 15px rgba(59, 130, 246, 0.9)",
                    "0 0 10px rgba(59, 130, 246, 0.7)",
                    "0 0 5px rgba(59, 130, 246, 0.5)",
                ],
                transition: {
                    duration: 2,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatType: "loop",
                },
            });
        };
        glowingAnimation();
    }, [buttonControls]);

    // 카드 애니메이션 효과
    useEffect(() => {
        cardControls.start({
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 },
        });
    }, [cardControls]);

    // 카드 애니메이션 효과 (마운트)
    useEffect(() => {
        let isMounted = true;
        const cardAnimation = async (): Promise<void> => {
            if (!isMounted) return;
            await cardControls.start({
                y: [30, 0],
                transition: { duration: 0.5 },
            });
            if (isMounted) {
                cardControls.start({
                    scale: 1,
                    transition: { duration: 0 },
                });
            }
        };
        cardAnimation();
        return () => { isMounted = false; };
    }, [cardControls]);

    const handleButtonClick = (): void => {
        buttonControls
            .start({
                scale: 0.95,
                transition: { duration: 0.1 },
            })
            .then(() => {
                buttonControls.start({
                    scale: 1,
                    transition: { duration: 0.2, type: "spring", stiffness: 400 },
                });
            });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.trim().toLowerCase();
        setUsername(value);
        setError("");
        if (searchCompleted) setSearchCompleted(false);
    };

    // 검색 폼 제출
    const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
        if (e) e.preventDefault();

        const normalizedUsername = username.trim().toLowerCase();

        if (isLoading) return;

        // 1. 비회원이고 검색어가 없는 경우
        if (!session && !normalizedUsername) {
            toast({
                title: "로그인 필요",
                description: "계정을 검색하고 메시지를 확인하려면 먼저 로그인해주세요.",
            });
            navigate("/auth");
            return;
        }

        // 2. 검색어가 없는 경우 (회원 포함)
        if (!normalizedUsername) {
            toast({
                title: "입력 필요",
                description: "인스타그램 사용자명을 입력해주세요.",
                variant: "destructive",
            });
            return;
        }

        // 3. 검색어 형식이 유효하지 않은 경우
        if (!isValidInstagramUsername(normalizedUsername)) {
            toast({
                title: "입력 오류",
                description: "유효하지 않은 인스타그램 ID 입니다. 영문, 숫자, 밑줄(_), 마침표(.)만 사용 가능합니다.",
                variant: "destructive",
            });
            return;
        }

        // 4. 모든 검증 통과 후, 만약 비회원 상태로 여기까지 왔다면 (예: URL 직접 접근 후 검색 시도 등)
        // 한 번 더 로그인 페이지로 안내 (보통은 1번에서 걸러짐)
        if (!session) {
            toast({
                title: "로그인 필요",
                description: "메시지를 확인하려면 로그인이 필요합니다. 로그인 페이지로 이동합니다.",
            });
            navigate("/auth");
            setIsLoading(false); // 로딩 상태 해제
            return;
        }

        // 결제 안내 토스트를 표시하고 요금제 페이지로 이동
        toast({
            title: "결제 후 이용 가능합니다",
            description: "인스타그램 메시지 확인 서비스는 프리미엄 기능으로, 요금제 구독 후 이용 가능합니다.",
            variant: "default"
        });

        // 잠시 로딩 표시 후 요금제 페이지로 이동
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            navigate("/pricing");
        }, 800);
    };

    // 여기서 다른 페이지 렌더링 시작

    return (
        <div className="pb-24 flex min-h-screen flex-col bg-gradient-to-br from-background to-background/80">
            <div className="absolute inset-0 grid-pattern opacity-30"></div>

            <header className="container relative z-10 flex items-center justify-between py-5">
                <div className="flex items-center gap-2">
                    <img
                        src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
                        alt="INSTA LAB AI Logo"
                        className="h-10 w-10 object-contain"
                    />
                    <h1 className="text-xl font-bold">INSTA LAB AI</h1>
                </div>
                <div className="flex items-center gap-2">
                    {session && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/profile")}
                            className="text-sm"
                        >
                            <User className="mr-1.5 h-4 w-4" />
                            프로필
                        </Button>
                    )}
                    <ThemeToggle />
                </div>
            </header>

            <main className="container relative z-10 flex flex-1 flex-col px-4 py-6 md:py-10">
                <div className="mx-auto w-full max-w-3xl animate-fade-in space-y-8">
                    <div className="space-y-2 text-center">
                        <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-sm">
                            <MessageCircle className="h-8 w-8 text-primary" />
                        </div>
                        <h1 className="gradient-heading text-2xl font-bold tracking-tight md:text-3xl">
                            내 인스타ID에 온 메시지 확인
                        </h1>
                        <p className="text-muted-foreground max-w-md mx-auto">
                            누군가 내 인스타그램 계정으로 대화한 <br />모든 메시지를 쉽고 안전하게 확인하세요.
                        </p>
                        <div className="mt-2">
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
                                프리미엄 기능
                            </Badge>
                        </div>
                    </div>

                    <motion.div
                        className="relative w-full"
                        initial={{ opacity: 0, y: 30 }}
                        animate={cardControls}
                        whileTap={{ scale: 0.99 }}
                    >
                        <Card className="relative card overflow-hidden rounded-[2.25rem] border border-[#292929] bg-[#151419]/80 backdrop-blur-xl shadow-2xl">
                            <motion.div
                                className="absolute inset-0 -z-10"
                                animate={{
                                    background: [
                                        "radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.4) 0%, rgba(124, 58, 237, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                                        "radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.4) 0%, rgba(168, 85, 247, 0.2) 30%, rgba(79, 70, 229, 0.1) 60%, transparent 80%)",
                                        "radial-gradient(circle at 50% 40%, rgba(168, 85, 247, 0.4) 0%, rgba(79, 70, 229, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                                        "radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.4) 0%, rgba(124, 58, 237, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                                    ],
                                }}
                                transition={{
                                    duration: 15,
                                    ease: "easeInOut",
                                    repeat: Infinity,
                                    repeatType: "loop",
                                }}
                                style={{
                                    borderRadius: "2.15rem",
                                    filter: "blur(2px)",
                                    mixBlendMode: "screen",
                                }}
                            />

                            <CardContent className="pt-10 pb-8 px-8">
                                {!searchCompleted ? (
                                    <form onSubmit={handleSubmit} className="relative space-y-4">
                                        <div className="relative">
                                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">
                                                {isLoading ? (
                                                    <Loader2 className="h-5 w-5 animate-spin" />
                                                ) : (
                                                    <span>@</span>
                                                )}
                                            </div>
                                            <Input
                                                placeholder={currentPlaceholder}
                                                value={username}
                                                onChange={handleInputChange}
                                                className="pl-10 pr-12 py-5 text-lg bg-transparent border border-[#363636] rounded-full focus-visible:ring-2 focus-visible:ring-primary focus:border-transparent transition-colors"
                                                autoComplete="off"
                                                autoFocus
                                                disabled={isLoading}
                                            />
                                            {username && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setUsername("");
                                                        setSearchCompleted(false);
                                                    }}
                                                    className="absolute inset-y-0 right-12 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            )}
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                <img
                                                    src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
                                                    alt="INSTA LAB AI Logo"
                                                    className="h-10 w-10 object-contain"
                                                />
                                            </div>
                                        </div>
                                        {error && (
                                            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                                                {error}
                                            </div>
                                        )}
                                    </form>
                                ) : (
                                    <div className="space-y-6 text-center py-4">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                                                <MessageCircle className="h-10 w-10 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-semibold mb-1">@{username}</h3>
                                                <p className="text-muted-foreground">계정 메시지 확인 완료</p>
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-border p-4 bg-background/30 backdrop-blur text-center">
                                            <div className="flex justify-center items-center mb-4">
                                                <Eye className="h-5 w-5 text-primary mr-2" />
                                                <span className="font-semibold">메시지 확인 완료</span>
                                            </div>
                                            <p className="text-lg mb-2">
                                                이 인스타그램에 도착한 모든 채팅메시지를 확인할 수 있습니다.
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                총 45개의 메시지가 발견되었습니다.
                                            </p>
                                        </div>

                                        <div className="mt-6 flex justify-center gap-4">
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setSearchCompleted(false);
                                                    setUsername("");
                                                }}
                                            >
                                                <Search className="h-4 w-4 mr-2" />
                                                다른 계정 검색
                                            </Button>
                                            <Button
                                                onClick={() => navigate(`/chat?username=${username}`)}
                                            >
                                                <MessageCircle className="h-4 w-4 mr-2" />
                                                메시지 보기
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>

                            {!searchCompleted && (
                                <CardFooter className="flex flex-col gap-4 pt-0">
                                    <motion.div
                                        animate={buttonControls}
                                        whileTap={{ scale: 0.97 }}
                                        className="w-full relative"
                                        style={{
                                            borderRadius: "0.5rem",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <motion.div
                                            className="absolute inset-0 rounded-md"
                                            animate={{
                                                background: [
                                                    "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                                                    "linear-gradient(120deg, #ec4899 0%, #4f46e5 50%, #a855f7 100%)",
                                                    "linear-gradient(120deg, #a855f7 0%, #ec4899 50%, #4f46e5 100%)",
                                                    "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                                                ],
                                            }}
                                            transition={{
                                                duration: 5,
                                                repeat: Infinity,
                                                repeatType: "loop",
                                            }}
                                            style={{
                                                padding: "2px",
                                                zIndex: 0,
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            className="w-full py-6 text-lg font-medium relative z-10 bg-background"
                                            disabled={isLoading}
                                            size="lg"
                                            onClick={() => {
                                                handleButtonClick();
                                                handleSubmit();
                                            }}
                                            style={{
                                                margin: "1px",
                                                backgroundClip: "padding-box",
                                            }}
                                        >
                                            <motion.div
                                                className="absolute inset-0 -z-10 rounded-md overflow-hidden"
                                                animate={{
                                                    background: [
                                                        "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                                                        "linear-gradient(120deg, #ec4899 0%, #4f46e5 50%, #a855f7 100%)",
                                                        "linear-gradient(120deg, #a855f7 0%, #ec4899 50%, #4f46e5 100%)",
                                                        "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                                                    ],
                                                }}
                                                transition={{
                                                    duration: 5,
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                }}
                                            />
                                            {isLoading ? (
                                                <>
                                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                    요금제 페이지로 이동 중...
                                                </>
                                            ) : (
                                                <>
                                                    <MessageCircle className="mr-2 h-5 w-5" />
                                                    메시지 확인
                                                </>
                                            )}
                                            {!session && (
                                                <Badge variant="outline" className="ml-2 bg-white/10">
                                                    로그인 필요
                                                </Badge>
                                            )}
                                        </Button>
                                    </motion.div>
                                </CardFooter>
                            )}
                        </Card>
                    </motion.div>

                    {!searchCompleted && (
                        <div className="space-y-8 mt-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 border rounded-xl bg-background/40 backdrop-blur-sm">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Shield className="h-5 w-5 text-primary" />
                                        <h3 className="font-semibold">100% 안전한 메시지 확인</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        누군가 내 인스타그램 아이디로 채팅한 메시지를 볼 수 있어요. 단 메시지 발송인은 인스타챗의 개인정보 보호 정책에 의해 공개할 수 없습니다.



                                    </p>
                                </div>


                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>서비스 신뢰도 지표</CardTitle>
                                        <p className="text-sm text-muted-foreground">
                                            2024년 1월부터 5월까지의 서비스 신뢰도 지표
                                        </p>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer config={trustChartConfig}>
                                            <ResponsiveContainer width="100%" height={240}>
                                                <AreaChart
                                                    data={trustChartData}
                                                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis
                                                        dataKey="month"
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tickMargin={8}
                                                    />
                                                    <YAxis
                                                        tickLine={false}
                                                        axisLine={false}
                                                        tickMargin={8}
                                                        domain={[80, 100]}
                                                    />
                                                    <ChartTooltip
                                                        cursor={false}
                                                        content={<ChartTooltipContent indicator="dot" />}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="안정성"
                                                        stackId="1"
                                                        stroke="var(--color-안정성)"
                                                        fill="var(--color-안정성)"
                                                        fillOpacity={0.4}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="보안성"
                                                        stackId="2"
                                                        stroke="var(--color-보안성)"
                                                        fill="var(--color-보안성)"
                                                        fillOpacity={0.4}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="이용자만족도"
                                                        stackId="3"
                                                        stroke="var(--color-이용자만족도)"
                                                        fill="var(--color-이용자만족도)"
                                                        fillOpacity={0.4}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </ChartContainer>
                                    </CardContent>
                                    <CardFooter>
                                        <div className="flex w-full items-start gap-2 text-sm">
                                            <div className="grid gap-2">
                                                <div className="flex items-center gap-2 font-medium leading-none">
                                                    지난 분기 대비 5.2% 상승 <TrendingUp className="h-4 w-4" />
                                                </div>
                                                <div className="flex items-center gap-2 leading-none text-muted-foreground">
                                                    최근 5개월 간의 신뢰도 데이터 (100점 만점)
                                                </div>
                                            </div>
                                        </div>
                                    </CardFooter>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>인스타그램 메시지 관리 신뢰성</CardTitle>
                                        <p className="text-sm text-muted-foreground">현재 서비스 안정성 통계</p>
                                    </CardHeader>
                                    <CardContent className="pb-4">
                                        <div className="h-[240px] flex justify-center items-center">
                                            <ChartContainer config={trustChartConfig} className="w-full h-full flex items-center justify-center">
                                                <ResponsiveContainer width="100%" height={240}>
                                                    <RadarChart cx="50%" cy="50%" outerRadius={90} data={usageChartData}>
                                                        <PolarGrid />
                                                        <PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }} />
                                                        <Radar
                                                            name="서비스 지표"
                                                            dataKey="value"
                                                            stroke="hsl(var(--primary))"
                                                            fill="hsl(var(--primary))"
                                                            fillOpacity={0.5}
                                                        />
                                                        <Tooltip content={<ChartTooltipContent />} />
                                                    </RadarChart>
                                                </ResponsiveContainer>
                                            </ChartContainer>
                                        </div>
                                    </CardContent>
                                    <CardFooter className="flex-col gap-2 text-sm">
                                        <div className="flex gap-2 font-medium leading-none">
                                            매월 독립 기관을 통한 신뢰도 검증 완료
                                        </div>
                                        <div className="leading-none text-muted-foreground">
                                            서비스 신뢰도 평균 96.6% (업계 최고 수준)
                                        </div>
                                    </CardFooter>
                                </Card>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <MobileNav activeItem="messages" />
        </div>
    );
} 